import React, { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import Panel from 'components/Panel/Panel';
import { TypographyComponent as TypographyElement, Icon } from '@tvg/fuse-design';

function DialogPanel(props: {
  active: boolean;
  transitionDuration: number;
  children: ReactElement[];
  dataTestId: string;
}) {
  const { active, transitionDuration, children, dataTestId } = props;

  return (
    <Panel
      active={active}
      offScreenPosition="right"
      transitionDuration={transitionDuration}
      onExited={(element: HTMLElement) => {
        element.scrollTop = 0;
      }}
      dataTestId={dataTestId}
    >
      {children}
    </Panel>
  );
}

const DialogSection = styled.div`
  padding: var(--spacing-space24);
`;

const DialogSectionIcon = styled(Icon)`
  margin-bottom: ${(props) => (props.removeMargin ? '0' : 'var(--spacing-space24)')};
  background-color: var(--color-brand-mid);
`;

const emphasisedText = css`
  em {
    color: var(--color-brand-mid);
    font-style: normal;
  }
`;

const DialogSectionHeading = styled(TypographyElement)`
  margin-bottom: var(--spacing-space32);
  ${emphasisedText}
`;

const DialogSectionSubHeading = styled(TypographyElement)`
  margin-bottom: var(--spacing-space16);
`;

const DialogText = styled(TypographyElement).attrs({
  type: 'paragraph',
  size: 'm1',
})`
  ${emphasisedText}
`;

const DialogList = styled.ul``;

const DialogListItem = styled.li`
  position: relative;
  margin-bottom: var(--spacing-space16);
  padding-left: var(--spacing-space24);

  &:before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    position: absolute;
    top: 6px;
    left: 0;
    border-radius: 100%;
    background: var(--color-brand-mid);
  }
`;

const AprSection = styled.div`
  padding-top: var(--spacing-space16);
`;

const AprText = styled(TypographyElement).attrs({
  tag: 'p',
  size: '1',
})`
  text-align: center;
`;

const HowToUseSection = styled(DialogSection)`
  padding: var(--spacing-space24);
  background-color: var(--color-global-grey200);
  text-align: center;
`;

const HowToUseList = styled.ol`
  display: flex;
  flex-direction: column;
  text-align: center;
  counter-reset: howToUseCounter;
`;

const HowToUseListItem = styled.li`
  flex-grow: 1;
  width: 100%;
  margin-bottom: var(--spacing-space32);
  counter-increment: howToUseCounter;

  &:last-child {
    margin-bottom: 0;
  }
`;

const HowToUseListItemHeading = styled(TypographyElement).attrs({
  tag: 'h5',
  size: '1',
})`
  margin-bottom: var(--spacing-space16);

  :before {
    content: counter(howToUseCounter) '. ';
  }
`;

const HowToUseListItemIconWrapper = styled.div`
  display: inline-block;
  width: 118px;
  height: 118px;
  margin-bottom: var(--spacing-space16);
  background: #fff;
  border-radius: 50%;
`;

const HowToUseListItemIcon = styled(Icon)`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
  background-color: var(--color-brand-mid);
`;

function HowToUseStep({
  icon,
  heading,
  description,
  size,
  height,
  width,
}: {
  icon: ReactElement;
  size: string;
  heading: string;
  description: string;
  height?: string;
  width?: string;
}) {
  return (
    <HowToUseListItem>
      <HowToUseListItemHeading>{heading}</HowToUseListItemHeading>

      <HowToUseListItemIconWrapper>
        <HowToUseListItemIcon icon={icon} alt={heading} size={size} height={height} width={width} />
      </HowToUseListItemIconWrapper>

      <DialogText>{description}</DialogText>
    </HowToUseListItem>
  );
}

const Question = styled.div`
  &:not(:last-child) {
    margin-bottom: var(--spacing-space32);
  }
`;

export {
  DialogSectionSubHeading,
  DialogPanel,
  DialogSection,
  DialogSectionIcon,
  DialogSectionHeading,
  DialogText,
  DialogList,
  DialogListItem,
  AprSection,
  AprText,
  HowToUseSection,
  HowToUseList,
  HowToUseStep,
  Question,
};
